<template>
  <transition :name="paginated ? 'paginated' : ''">
    <!-- Binding to keydown in purpose, see _onEnterKey() in RfForm.vue -->
    <div class="rf-section" :class="computedClass" v-show="!paginated || isActive" @keydown.enter="_onEnterKey">
      <slot name="title"></slot>
      <transition :name="paginated ? '' : 'section'">
        <div class="rf-section-content" v-show="isActive || isComplete">
          <slot></slot>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import getFields from '../mixins/getFields'
import parentForm from '../mixins/parentForm'

export default {
  name: 'RfSection',
  mixins: [getFields, parentForm],
  props: {
    paginated: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      default: null
    },
    scrollOffset: {
      type: [Number, String],
      default: 0
    },
    validateOnFieldChange: {
      type: Boolean,
      default: false
    },
    // Whether the first field should be focused when the section becomes active
    enableFocusFirstField: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isActive: false,
      isComplete: false,
      isValid: false
    }
  },
  computed: {
    computedClass () {
      return { 'is-active': this.isActive, 'is-complete': this.isComplete }
    },
    normalizedScrollOffset () {
      return parseInt(this.scrollOffset, 10)
    }
  },
  created () {
    this.componentType = 'rf-section'
  },
  mounted () {
    this.parentForm.$emit('section:added', this)
    // If section has no fields, or no fields with any validation rules, consider it valid
    let fieldRules = this.getFields().map(vm => vm.$validator.fields.find({ name: vm.name }).rules)
    let hasAFieldWithRules = fieldRules.filter(ruleObject => Object.keys(ruleObject).length).length

    if (!this.getFields().length) {
      this.isValid = true
    } else if (!hasAFieldWithRules) {
      this.isValid = true
    }
    if (this.validateOnFieldChange) {
      this.parentForm.$on(['field:changed'], (name, newVal, oldVal) => {
        if (this._isMyChild(name)) {
          this.validate()
        }
      })
    }
  },
  destroyed () {
    this.parentForm.$emit('section:destroyed', this)
  },
  methods: {
    /**
     * Note: not providing an argument to this method effectively means
     * "decide whether to show a field's validation UI based on whether it's
     * already showing". If an argument IS provided, it will truly FORCE
     * all of the section's fields UI validation to show
    */
    async validate (forceShowUiValidation = null) {
      let errors = await Promise.all(this.getFields().map(async vm => {
        // If no parameter provided, show the fields UI validation only if it's
        // already showing
        let showUiValidation = forceShowUiValidation !== null ? forceShowUiValidation : !!(vm.showInvalid || vm.showValid)

        return vm.validate(showUiValidation)
      }))
      // Remove all non-false responses, and if any are left, we have errors
      let hasErrors = errors.filter(res => !res).length
      this.isValid = !hasErrors

      if (hasErrors) {
        this.parentForm.$emit('section:error', this)
      }

      return hasErrors
    },
    clearValidation () {
      this.getFields().forEach(function (vm) {
        vm.clearValidation()
      })
    },
    reset () {
      this.getFields().forEach(function (vm) {
        vm.reset()
      })
    },
    activate () {
      this.isActive = true
    },
    deactivate () {
      this.isActive = false
    },
    focusFirstField () {
      if (this.enableFocusFirstField) {
        let fields = this.getFields()
        if (fields.length) {
          fields[0].focus()
        }
      }
    },
    complete () {
      this.isComplete = true
      this.isActive = false
    },
    _onEnterKey (e) {
      if (e.target.tagName !== 'TEXTAREA') {
        e.preventDefault()
        e.stopPropagation()
        this.parentForm.$emit('section:enter-key', this, e)
      }
    },
    _isMyChild (name) {
      return this.getFields().map(vm => vm.name).indexOf(name) > -1
    }
  }
}
</script>



// WEBPACK FOOTER //
// RfSection.vue?599ff51c